import React, { useEffect, useRef } from 'react';
import { resolveQuoteAttention } from '../services/quote-attention';

interface Stage1PostProcessingContext {
    projectId: string | null;
    loadGeneration: number;
    currentPage: string;
    stage: number;
    generationActive: boolean;
    formData: any;
}

export interface UseStage1PostProcessingParams {
    crmIdentity?: { companyId: string | null; propertyId: string | null };
    photos?: any[];
    documents?: any[];
    videos?: any[];
    videoTranscripts?: any[];
    preprocessedFiles?: any[];
    formData: any;
    isStage1Processing: boolean;
    currentPage: string;
    stage: number;
    projectId?: string | null;
    loadGeneration?: number;
    getLiveContext?: () => Stage1PostProcessingContext;
    setIsPostProcessing: (val: boolean) => void;
    setPostProcessContext: (ctx: any) => void;
    setFormData: React.Dispatch<React.SetStateAction<any>>;
}

export function useStage1PostProcessing({
    crmIdentity,
    photos,
    documents,
    videos,
    videoTranscripts,
    preprocessedFiles,
    formData,
    isStage1Processing,
    currentPage,
    stage,
    projectId = null,
    loadGeneration = 0,
    getLiveContext,
    setIsPostProcessing,
    setPostProcessContext,
    setFormData,
}: UseStage1PostProcessingParams): void {
    const postProcessDebounceRef = useRef<any>(null);
    const liveContextRef = useRef(getLiveContext);
    const crmIdentityRef = useRef(crmIdentity);
    crmIdentityRef.current = crmIdentity;
    liveContextRef.current = getLiveContext || (() => ({ projectId, loadGeneration, currentPage, stage, generationActive: false, formData }));

    useEffect(() => {
        const hasFiles = (photos?.length || 0) + (documents?.length || 0) + (videos?.length || 0) > 0;
        const hasFormData = !!(formData?.address || formData?.clientName || formData?.notes);
        if (!hasFiles && !hasFormData) return;
        if (isStage1Processing) return;
        if (currentPage !== 'quotemaker' || stage !== 1) return;
        const captured = liveContextRef.current!();
        const selectedCompanyId = crmIdentity?.companyId || null;
        const selectedPropertyId = crmIdentity?.propertyId || null;
        let cancelled = false;
        let started = false;
        const ownsResult = () => {
            const live = liveContextRef.current!();
            return !cancelled && live.projectId === captured.projectId && live.loadGeneration === captured.loadGeneration
                && live.currentPage === 'quotemaker' && live.stage === 1 && !live.generationActive
                && (crmIdentityRef.current?.companyId || null) === selectedCompanyId
                && (crmIdentityRef.current?.propertyId || null) === selectedPropertyId
                && live.formData === formData;
        };

        // Debounce: wait 1500ms after the last change before re-running post-process
        if (postProcessDebounceRef.current) clearTimeout(postProcessDebounceRef.current);
        postProcessDebounceRef.current = setTimeout(async () => {
            if (!ownsResult()) return;
            started = true;
            setIsPostProcessing(true);
            try {
                const { postProcessImport } = await import('../services/postprocessing-pipeline');
                if (!ownsResult()) return;
                const ctx = await postProcessImport({
                    crmIdentity: { companyId: selectedCompanyId, propertyId: selectedPropertyId },
                    formData,
                    photos: photos || [],
                    documents: documents || [],
                    videos: videos || [],
                    videoTranscripts: videoTranscripts || [],
                    preprocessedFiles: preprocessedFiles || [],
                });
                if (!ownsResult()) return;
                setPostProcessContext(ctx);
                console.log('[QM] Post-processing complete:', ctx);

                // Phase 3C Gap #8 Layer A (2026-05-04): auto-fill
                // formData.attentionTo from the resolved primary contact when
                // the field is empty. Manual override discipline: NEVER
                // clobber an existing typed value (matches the
                // `prev.X || extracted.X` pattern at App.jsx:1576-1581).
                // The role-priority selection (PRIMARY > is_primary > first)
                // happens at postprocessing-pipeline.js:238 (Gap #8 fix).
                try {
                    const primaryName = selectedCompanyId && ctx?.clientMatch?.id === selectedCompanyId
                        ? ctx.clientMatch.primaryContact?.name : null;
                    if (!ownsResult()) return;
                    const rfqAttention = resolveQuoteAttention({ ...formData, attentionTo: '', attentionToSource: '' });
                    if (rfqAttention.name || primaryName) {
                        setFormData((prev: any) => {
                            // React may run this updater after the async owner
                            // was invalidated. Never apply a captured RFQ/CRM
                            // result to a new project or a newer manual form.
                            if (!ownsResult() || prev !== formData) return prev;
                            const current = String(prev?.attentionTo || '').trim();
                            const currentSource = prev?.attentionToSource || '';
                            if (current && currentSource === 'manual') {
                                return prev;
                            }
                            if (rfqAttention.name && (!current || currentSource === 'crm-primary-contact')) {
                                return {
                                    ...prev,
                                    attentionTo: rfqAttention.name,
                                    attentionToSource: rfqAttention.source,
                                };
                            }
                            if (current) {
                                return prev;
                            }
                            return {
                                ...prev,
                                attentionTo: primaryName,
                                attentionToSource: 'crm-primary-contact',
                            };
                        });
                    }
                } catch (atErr: any) {
                    console.warn('[QM] attentionTo auto-fill skipped (non-fatal):', atErr?.message);
                }
            } catch (err: any) {
                console.warn('[QM] Post-processing failed:', err?.message);
            } finally {
                if (!cancelled) setIsPostProcessing(false);
            }
        }, 1500);

        return () => {
            cancelled = true;
            if (postProcessDebounceRef.current) clearTimeout(postProcessDebounceRef.current);
            if (started) setIsPostProcessing(false);
        };
    // Note: preprocessedFiles?.length intentionally NOT in deps - it changes during
    // bulk imports and would cascade re-renders + reset the debounce timer for every file.
    // The debounce on photos/documents/videos counts is enough to wait until import settles.
    }, [
        photos?.length,
        documents?.length,
        videos?.length,
        formData?.address,
        formData?.clientCompany,
        formData?.clientName,
        formData?.strataplan,
        formData?.jobDescription,
        formData?.notes,
        isStage1Processing,
        currentPage,
        stage,
        projectId,
        loadGeneration,
        crmIdentity?.companyId,
        crmIdentity?.propertyId,
    ]);

    // Reset post-processing context on resetForm
    useEffect(() => {
        if (!photos?.length && !documents?.length && !videos?.length && !formData?.address && !formData?.clientName) {
            setPostProcessContext(null);
        }
    }, [photos?.length, documents?.length, videos?.length, formData?.address, formData?.clientName]);
}
